import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { SafeUrl, Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { IconButtonComponent } from "@tylertech/forge";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor } from "@tylertech/logging";
import { LOCATION, Utilities, WINDOW } from "@tylertech/shared-module";
import { IAppRegistrationModel, Key, ModifierKey } from "@tylertech/tyler-api-types";
import { KeyHandlerService, NavMenuService, NavigationService, OmniBarService, SecurityService } from "../../services";

@Component({
    selector: "omni",
    templateUrl: "./omniBar.tmpl.html",
    styleUrls: [
        "./omniBar.component.scss"
    ],
    standalone: false
})
export class OmniBarComponent implements OnDestroy, OnInit
{
    /** @deprecated - The context for this functionality should be inverted. Boolean states should never default to `true` */
    @Input() readonly backBar: boolean;
    @Input() readonly commandKey: string;
    @Input() readonly rootApp: string;

    @ViewChild("omnibarMenu") readonly omnibarMenuRef: ElementRef<IconButtonComponent>;

    private readonly destructor$ = new Subject<void>();

    private listenerId: string;
    private readonly log = getLoggerFor("OmniBarComponent");

    apps: IAppRegistrationModel[] = [];
    brand: string;
    embedded: boolean;
    menuDisabled: boolean = true;
    authenticated: boolean;
    isEscalationChat: boolean;
    isVendorUser: boolean;
    omniTitle: string;

    constructor(
        @Inject(WINDOW) private readonly window: globalThis.Window,
        @Inject(LOCATION) private readonly location: Location,
        private readonly cdr: ChangeDetectorRef,
        private readonly router: Router,
        private readonly $titleService: Title,
        private readonly $navMenuService: NavMenuService,
        private readonly $omniBarService: OmniBarService,
        private readonly $securityService: SecurityService,
        private readonly $navigationService: NavigationService,
        private readonly $keyHandlerService: KeyHandlerService,
        private readonly $shellService: ShellService
    )
    {
        this.embedded = $shellService.isEmbedded();
        this.isEscalationChat = this.location.href.indexOf("escalation-chat") > -1;
    }

    private setupObservables(): void
    {
        const self = this;

        this.$navMenuService.disabled
            .pipe(takeUntil(this.destructor$))
            .subscribe((disabled) =>
            {
                this.menuDisabled = disabled;
            });

        this.$navMenuService.toggleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe((open: boolean) =>
            {
                if (!open)
                {
                    this.omnibarMenuRef?.nativeElement?.focus(undefined);
                }
            });

        this.$securityService.principleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe((newPrinciple) =>
            {
                this.log.debug(`SecurityService.principleChange`, { newPrinciple });
                this.authenticated = this.$securityService.isAuthenticated;
                this.isVendorUser = this.$securityService.isVendorUser;
                setTimeout(()=> updateTitle(this.$omniBarService.title), 100);
            });

        this.$omniBarService.titleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe(newTitle =>
            {
                this.log.debug(`OmniBarService.titleChange`, { newTitle });
                updateTitle(newTitle);
            });

        function updateTitle(newTitle: string): void
        {
            self.omniTitle = self.authenticated
                ? (!!newTitle ? newTitle : self.$titleService.getTitle())
                : "";
            if (self.omniTitle === "Login")
                self.omniTitle = "";
            self.cdr.detectChanges();
        }
    }

    get isProvisional(): boolean
    {
        return this.$securityService.isProvisional;
    }

    get isEmbedded(): boolean
    {
        return this.embedded;
    }

    get showBackBar(): boolean
    {
        return this.backBar ?? true;
    }

    get omniBarClasses(): Record<string, boolean>
    {
        const classes = {};
        const className = `app-bar--${this.$omniBarService.colorClass}`;
        classes[className] = true;
        return classes;
    }

    get allowSearch(): boolean
    {
        return !this.isVendorUser;
    }

    @HostListener("window:keydown", ["$event"])
    onKeydown($event: KeyboardEvent): void
    {
        this.$keyHandlerService.handleKeyDown($event);
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
        this.$keyHandlerService.unsubscribe(Key.F2, ModifierKey.Ctrl);
    }

    ngOnInit(): void
    {
        this.setupObservables();
        this.listenerId = Utilities.generateRandomGuid();
        // TODO(DO-4549) - convert to hostListener
        this.$keyHandlerService.subscribe(
            this.listenerId,
            Key.F2,
            ModifierKey.Ctrl,
            "Global|Open/close navigation menu",
            () =>
            {
                if (!this.menuDisabled)
                {
                    this.$navMenuService.toggle();
                }
            }
        );
    }

    back(): void
    {
        this.window.history.back();
    }

    getDefaultAppUrl(): SafeUrl
    {
        // TODO - shouldn't we rely on the backend to tell us what the default app for the current user is?
        // TODO - shouldn't this URL be built once when the component is initialized?
        let app = "dashboard";
        if (!this.$securityService.principle)
        {
            return "";
        }

        if (this.$securityService.isVendorUser)
        {
            app = "vendormanager";
        }

        if (this.$shellService.isEmbedded())
        {
            // TODO - why do we need to add a route param to detect the embedded state?
            const embedded = "/1";
            app = app + embedded;
        }

        return this.$navigationService.getLinkToApp(app);
    }

    reload(): void
    {
        if (this.rootApp)
        {
            this.router.navigateByUrl(this.$navigationService.getLinkToApp(this.rootApp) as string);
            return;
        }

        this.location.reload();
    }

    toggleMenu(): void
    {
        this.$navMenuService.toggle();
    }
}
